/*
PURPOSE: Reusable button component for adding contributions to comments

USE CASES:
- "Contribute" button in top-level CommentCard
- "Add a contribution" button at the bottom of ReplyThread
- Any future contribution/reply actions in the comment system

DOMAIN BOUNDARIES:
- Handles visual presentation of contribution actions
- Supports flexible text display modes
- Should NOT handle API calls or comment creation logic

ASSUMPTIONS:
- Gray color scheme for neutral interaction state
- Text can be hidden or show different contribution messages
- Size variants align with comment hierarchy design
*/

import React from 'react';
import MessageCirclePlusIcon from '../MessageCirclePlusIcon/MessageCirclePlusIcon';

interface ContributeButtonProps {
  /** Click handler */
  onClick: () => void;
  /** Text display mode */
  textMode?: 'none' | 'contribute' | 'add-contribution' | 'respond' | 'add-response';
  /** Size variant */
  size?: 'primary' | 'secondary';
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Custom aria-label */
  ariaLabel?: string;
  /** Test ID attributes */
  testId?: string;
  commentId?: string;
}

const ContributeButton: React.FC<ContributeButtonProps> = ({
  onClick,
  textMode = 'contribute',
  size = 'primary',
  disabled = false,
  className = '',
  ariaLabel,
  testId = 'contribute-button',
  commentId
}) => {
  // Size configurations
  const sizeConfig = {
    primary: {
      iconSize: 20,
      textSize: 'text-sm',
      textWeight: 'font-extralight',
      gap: 'gap-1',
      iconMargin: 'mr-1'
    },
    secondary: {
      iconSize: 17,
      textSize: 'text-xs',
      textWeight: 'font-extralight',
      gap: 'gap-1',
      iconMargin: 'mr-3'
    }
  };

  const config = sizeConfig[size];

  // Determine text to display
  const getButtonText = () => {
    switch (textMode) {
      case 'none':
        return null;
      case 'contribute':
        return 'Contribute';
      case 'add-contribution':
        return 'Add a contribution';
      case 'respond':
        return 'Respond';
      case 'add-response':
        return 'Add a response';
      default:
        return null;
    }
  };

  const buttonText = getButtonText();

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`group inline-flex items-center ${config.gap} transition-colors duration-200 text-gray-400/60 hover:text-coral-pink ${
        disabled ? 'cursor-not-allowed opacity-50' : ''
      } ${className}`}
      aria-label={ariaLabel || (buttonText || "Add contribution")}
      data-testid={testId}
      data-comment-id={commentId}
    >
      <MessageCirclePlusIcon 
        size={config.iconSize}
        className={`${buttonText ? config.iconMargin : ''} transition-colors duration-200`}
        color="currentColor"
      />
      {buttonText && (
        <span className={`${config.textWeight} ${config.textSize} transition-colors duration-200 leading-none`}>
          {buttonText}
        </span>
      )}
    </button>
  );
};

export default ContributeButton;